// src/pages/CustomReportPage.tsx
//
// ★★ WAVE 2b — Annex A story 13, "Generate custom reports": the field picker
// this product has never had a screen for.
//
// ══ WHAT WAS ACTUALLY MISSING, AND WHY IT COUNTED AS "NO SCREEN" ══
//
// `GET /api/reports/custom` has existed since Phase 5 Task 17 with a full
// catalogue, an injection defence and both formats. What did not exist was any
// way to reach it without hand-writing a query string:
// `docs/delivery/DEMO-READINESS-PLAN.md` §2 item 5 counts story 13 among "nine
// Must-Have stories with NO screen", and a report builder whose field list
// lives only in a TypeScript constant on the server is a builder no customer
// can operate.
//
// ══ THE ONE RULE THIS SCREEN IS BUILT AROUND ══
//
// ⚠ **The client decides NOTHING about what may be selected.** The field list,
// the bill types, the statuses, the formats, the export bound and the preview
// page size are all read from `GET /api/reports/custom/fields` — the server's
// own catalogue — and the ids are posted back untouched. That is deliberate and
// it is the security posture of the route this screen drives: there is exactly
// ONE place a `?fields=` string is resolved against the catalogue
// (`resolveRequestedFields`), and a second, client-side copy of that list would
// be a copy that drifts silently. The server would keep refusing an id the
// client stopped sending, and nothing would ever go red.
//
// ⚠ **The preview and the download send byte-identical parameters** — both go
// through `customReportQuery` in `lib/api.ts`, one function — because an
// officer who previews one population and downloads another has been told a
// quiet lie, which is the failure mode this repository's own working agreements
// name first.
//
// ⚠ **The preview is not the report.** It is bounded at the server's own
// `maxPreviewPageSize` and paged by offset; the complete, single-query
// population is the PDF or the Excel. Where a filter selects more rows than a
// document may carry, the screen says so BEFORE the officer generates anything
// — the export refuses with a 422 rather than truncating, and finding that out
// from a failed download is a worse way to learn it.
import { plainCode } from '../lib/plain-words'
import { useEffect, useMemo, useState } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { DatePicker } from '../components/DatePicker'
import { targetValue } from '../lib/sgds-dom'
import { RateProvenanceNotes } from '../components/RateProvenance'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

/*
  ★★★ THE PREVIEW'S HEADERS SORT — 2026-08-24, and this table is the one case in the
  sweep where the COLUMNS are not known at compile time.

  An officer picks the field set, so the header row is whatever `preview.columns`
  names. The sort key is therefore the catalogue FIELD ID the column already carries
  (`column.id`), which is the same id `?fields=` is resolved against on the server —
  so a header can only ever ask for a sort on a column that is on screen, and there
  is no second, looser whitelist anywhere in the path.

  ⚠ Sorted on the SERVER, and it had to be: the preview is offset-paged over
  `app.bill`, so ordering the fifty rows in hand would answer "the largest of these
  fifty" while looking exactly like "the largest bill this definition selects".

  ⚠ The DOWNLOADS do not take the sort, and that is deliberate rather than an
  oversight — `GET /api/reports/custom` states the reason: an extract two officers
  can reconcile against each other has one canonical order.
*/


/**
 * The fields a first-time visitor arrives with, so the screen shows a working
 * report rather than an empty table and an instruction.
 *
 * ⚠ **These are a DEFAULT SELECTION, never a catalogue.** Every id here is
 * checked against the server's catalogue before it is used
 * ({@link initialSelection}), so a field renamed or removed at the gateway
 * drops out of this default rather than being sent and refused. The screen can
 * never offer a field the server does not publish.
 */
const PREFERRED_DEFAULT_FIELDS = ['reference', 'agencyCode', 'billType', 'status', 'amount'] as const

function initialSelection(available: readonly string[]): string[] {
  const offered = new Set(available)
  const preferred = PREFERRED_DEFAULT_FIELDS.filter((id) => offered.has(id))
  // A catalogue that shares none of the preferred ids still has to produce a
  // usable report — the first field is a report, an empty selection is a 400.
  return preferred.length > 0 ? [...preferred] : available.slice(0, 1)
}

/**
 * ★★★ WAVE 27 — the SAME definition the download buttons send, as a flat query.
 *
 * ⚠ **BUILT FROM `appliedParams`, never from the live form.** The two downloads
 * already read the APPLIED definition rather than whatever is currently typed —
 * this screen's own note explains why: what arrives must be what was previewed. An
 * email built from the form would carry a definition the officer never saw, which
 * is the same defect with a longer delay before anyone notices.
 */
function emailQuery(applied: {
  fields: readonly string[]
  billType?: string
  status?: string
  serviceDateFrom?: string
  serviceDateTo?: string
} | null): Readonly<Record<string, string>> {
  if (applied === null) return {}
  return {
    fields: applied.fields.join(','),
    ...(applied.billType !== undefined && { billType: applied.billType }),
    ...(applied.status !== undefined && { status: applied.status }),
    ...(applied.serviceDateFrom !== undefined && { serviceDateFrom: applied.serviceDateFrom }),
    ...(applied.serviceDateTo !== undefined && { serviceDateTo: applied.serviceDateTo }),
  }
}

export function CustomReportPage() {
  const catalogue = useApi(() => api.customReportFields(), [])

  const [selected, setSelected] = useState<readonly string[]>([])
  /**
   * ⚠ **Whether the default selection has ALREADY been applied — and it is a
   * separate fact from "is anything selected".**
   *
   * The first draft keyed the default off `selected.length === 0`, which meant
   * the effect re-ran the moment an officer unticked the last box and silently
   * re-ticked all five. The selection could not be emptied, the "select at least
   * one field" refusal was unreachable, and the officer's own action was undone
   * with no message. Found by running the DOM test, not by reading the diff.
   */
  const [defaultsApplied, setDefaultsApplied] = useState(false)
  const [billType, setBillType] = useState('')
  const [status, setStatus] = useState('')
  const [serviceDateFrom, setServiceDateFrom] = useState('')
  const [serviceDateTo, setServiceDateTo] = useState('')
  const [pagerOffset, setPagerOffset] = useState(0)
  /**
   * ⚠ **The APPLIED definition, distinct from the one being edited.**
   *
   * Without this the preview would re-query on every keystroke of a date and on
   * every checkbox — one request per interaction against a `count(*)` over
   * `app.bill`. It is also wrong for the officer: a half-typed date range is a
   * different report, and watching the table flicker through it teaches nothing.
   * "Show preview" is the commit point, and the download buttons read from the
   * SAME applied definition, so the file can never be a different report from
   * the one on screen.
   */
  const [applied, setApplied] = useState<{
    fields: readonly string[]
    billType: string
    status: string
    serviceDateFrom: string
    serviceDateTo: string
  } | null>(null)

  const fields = useMemo(() => catalogue.data?.fields ?? [], [catalogue.data])
  const pageSize = catalogue.data?.previewPageSize ?? 25

  // The default selection can only be chosen once the catalogue has arrived —
  // see PREFERRED_DEFAULT_FIELDS' own note on why it is filtered against it.
  useEffect(() => {
    if (fields.length === 0 || defaultsApplied) return
    setSelected(initialSelection(fields.map((f) => f.id)))
    setDefaultsApplied(true)
  }, [fields, defaultsApplied])

  /*
    ⚠ `useSort<string>` rather than a key union, because the COLUMNS are the
    officer's own field selection and are not known at compile time. The key is
    the catalogue field id the column already carries, so a header can only ask
    for a sort on a column that is on screen.
  */
  const { sort, onSort, interacted } = useSort<string>()

  const preview = useApi(
    () =>
      applied === null
        ? Promise.resolve(null)
        : api.customReportPreview({
            fields: applied.fields,
            ...(applied.billType.length > 0 && { billType: applied.billType }),
            ...(applied.status.length > 0 && { status: applied.status }),
            ...(applied.serviceDateFrom.length > 0 && { serviceDateFrom: applied.serviceDateFrom }),
            ...(applied.serviceDateTo.length > 0 && { serviceDateTo: applied.serviceDateTo }),
            limit: pageSize,
            offset: pagerOffset,
            ...sortParams(sort),
          }),
    [
      applied?.fields.join(','),
      applied?.billType,
      applied?.status,
      applied?.serviceDateFrom,
      applied?.serviceDateTo,
      pageSize,
      pagerOffset,
      sort.column,
      sort.dir,
    ],
  )

  const rows = preview.data?.rows ?? []
  const columns = preview.data?.columns ?? []
  /*
    ⚠ A SORT CHANGE RESTARTS THE WALK. Page three of one order is not page three of
    another, so leaving the offset alone would land the officer in the middle of a
    preview they have not seen the top of — which on a surface whose whole job is
    "did I select the right shape" is the one thing that makes it useless.
  */
  const onColumnSort = (column: string) => {
    onSort(column)
    pager.reset()
    setPagerOffset(0)
  }
  const total = preview.data?.total ?? 0
  const pager = useOffsetPager(pageSize, total)

  // The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. One place, not two sources of one fact.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  const toggleField = (id: string) => {
    setSelected((current) => (current.includes(id) ? current.filter((f) => f !== id) : [...current, id]))
  }

  const canRun = selected.length > 0
  const appliedParams =
    applied === null
      ? null
      : {
          fields: applied.fields,
          ...(applied.billType.length > 0 && { billType: applied.billType }),
          ...(applied.status.length > 0 && { status: applied.status }),
          ...(applied.serviceDateFrom.length > 0 && { serviceDateFrom: applied.serviceDateFrom }),
          ...(applied.serviceDateTo.length > 0 && { serviceDateTo: applied.serviceDateTo }),
        }

  const applyDefinition = () => {
    if (!canRun) return
    // A new definition is a different population; page three of one is not page
    // three of another, so the walk restarts.
    pager.reset()
    setPagerOffset(0)
    setApplied({ fields: [...selected], billType, status, serviceDateFrom, serviceDateTo })
  }

  /**
   * ⚠ **The definition on screen has been EDITED since the preview was run.**
   *
   * The download buttons deliberately use the APPLIED definition, so what
   * arrives is always what was previewed. That is right, and it is also
   * confusing if the screen does not say so — hence the notice below rather
   * than silently downloading yesterday's selection.
   */
  const edited =
    applied !== null &&
    (applied.fields.join(',') !== selected.join(',') ||
      applied.billType !== billType ||
      applied.status !== status ||
      applied.serviceDateFrom !== serviceDateFrom ||
      applied.serviceDateTo !== serviceDateTo)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Custom report</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Choose the fields and the filters, preview the result, then download it as PDF or Excel. Every row is restricted to
          bills your active identity may already see: the same scope rule the Bills screen applies.
        </p>
      </div>

      {catalogue.error !== null ? (
        <ErrorCard title="Could not load the report field catalogue" message={catalogue.error} onRetry={catalogue.reload} />
      ) : catalogue.initialLoading ? (
        <LoadingCard label="Loading report fields" rows={3} />
      ) : (
        <div className="pt-card flex flex-col gap-5 pt-no-print">
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-lg mb-1">Fields</legend>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3">
              {selected.length} of {fields.length} selected. Columns appear in the order you tick them.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {fields.map((field) => (
                <label key={field.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(field.id)}
                    onChange={() => toggleField(field.id)}
                    aria-label={field.label}
                  />
                  {field.label}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <SgdsSelect label="Bill type (optional)" value={billType} onSgdsChange={(e) => setBillType(targetValue(e))}>
              <SgdsSelectOption value="">Any bill type</SgdsSelectOption>
              {(catalogue.data?.billTypes ?? []).map((t) => (
                <SgdsSelectOption key={t} value={t}>
                  {plainCode(t)}
                </SgdsSelectOption>
              ))}
            </SgdsSelect>
            <SgdsSelect label="Status (optional)" value={status} onSgdsChange={(e) => setStatus(targetValue(e))}>
              <SgdsSelectOption value="">Any status</SgdsSelectOption>
              {(catalogue.data?.statuses ?? []).map((t) => (
                <SgdsSelectOption key={t} value={t}>
                  {plainCode(t)}
                </SgdsSelectOption>
              ))}
            </SgdsSelect>
            {/*
              ⚠ SERVICE date, never submission date, and the labels say so. The
              entitlement that priced every one of these bills was evaluated as
              at the DATE OF MEDICAL SERVICE, so a range
              filter on the submission date would answer a different question
              while looking like the same one.
            */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold">Service date from (optional)</span>
              <DatePicker value={serviceDateFrom} onChange={setServiceDateFrom} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold">Service date to (optional)</span>
              <DatePicker value={serviceDateTo} onChange={setServiceDateTo} />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SgdsButton type="button" variant="primary" disabled={!canRun} loading={preview.loading} onClick={applyDefinition}>
              Show preview
            </SgdsButton>
            {/*
              ★★★ WAVE 27 — one control group, owner-directed. These answer a single
              question — "what do I want to do with this list?" — and were arranged as if
              they answered three or four. See `ExportToolbar` for the whole argument.
            */}
            <ExportToolbar
              onDownload={(format) => {
                /*
                 * ⚠ The APPLIED definition, never the live form — this screen's own
                 * note says what arrives must be what was previewed.
                 *
                 * ★★★ THIS USED TO BE `if (appliedParams !== null) …` AND NOTHING ELSE,
                 * which is the defect the owner reported: with no preview yet,
                 * pressing *Download PDF* fell off the end of the guard and produced
                 * no file, no message and no change of any kind, on a button that
                 * looked entirely live. `downloadBlockedReason` below now answers the
                 * press instead. The assertion here is a second belt: reaching this
                 * line with no applied definition would mean the toolbar let a
                 * blocked press through, and a throw is findable where a silent
                 * return was not.
                 */
                if (appliedParams === null) {
                  throw new Error('ExportToolbar invoked onDownload while downloadBlockedReason was set')
                }
                window.location.href = api.customReportUrl(format, appliedParams)
              }}
              {...(appliedParams === null
                ? {
                    downloadBlockedReason:
                      'Press Show preview first: the download carries the definition you previewed, so there is nothing to send until one has run.',
                  }
                : {})}
              email={
                <EmailReportButton
                  path="/api/reports/custom"
                  query={emailQuery(appliedParams)}
                  {...(appliedParams === null
                    ? { disabledReason: 'Preview the report first: the email carries the definition you previewed.' }
                    : {})}
                />
              }
            />
          </div>

          {!canRun && (
            <p className="text-sm text-[var(--sgds-danger-color-emphasis)] m-0" role="alert">
              Select at least one field. A report with no columns is not a report.
            </p>
          )}
          {appliedParams === null && canRun && (
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">
              The downloads carry the definition you previewed, so preview it first.
            </p>
          )}
          {edited && (
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0" role="status">
              The fields or filters above have changed since this preview ran. The downloads still carry the
              PREVIEWED definition: press <strong>Show preview</strong> to apply the new one.
            </p>
          )}
        </div>
      )}

      {preview.error !== null && <ErrorCard title="Could not run this report" message={preview.error} onRetry={preview.reload} />}

      {applied !== null && preview.error === null && (
        preview.initialLoading ? (
          <LoadingCard label="Running report" rows={5} />
        ) : rows.length === 0 ? (
          <EmptyCard
            title="No bills match this report"
            message="Nothing within your active identity's scope matches these filters. Widen the filters, or check that the service-date range covers the bills you expect."
          />
        ) : (
          <div className="pt-card p-0 overflow-hidden">
            {preview.data?.exceedsExportBound === true && (
              /*
                ⚠ Said BEFORE the download, not discovered from a failed one.
                The export refuses above its bound rather than truncating, which
                is the right behaviour and an opaque one if the first the
                officer hears of it is a browser download that produced nothing.
              */
              <p className="text-sm m-0 px-5 py-3 border-b border-[var(--sgds-border-color-muted)] text-[var(--sgds-danger-color-emphasis)]" role="alert">
                This filter selects {total} bills, more than the {preview.data.maxRows} a single document may
                carry. The downloads will be refused rather than silently cut short: narrow the service-date
                range, the bill type or the status first.
              </p>
            )}
            {/*
              ⚠ The column LABELS come from the payload, so they are built into a map
              here rather than being a constant: the live region has to speak the
              label the officer is actually looking at, and this table's columns are
              whatever field set they selected.
            */}
            <SortAnnouncement
              sort={sort}
              interacted={interacted}
              busy={preview.loading}
              columnLabels={Object.fromEntries(columns.map((column) => [column.id, column.label]))}
              rowCount={rows.length}
            />
            <div className="pt-table-wrap" aria-busy={preview.loading}>
              <table className="pt-table">
                <caption className="sr-only">
                  Preview of the bills this report definition selects within your scope, with the fields you chose as columns
                </caption>
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <SortableTh
                        key={column.id}
                        label={column.label}
                        sortKey={column.id}
                        sort={sort}
                        onSort={onColumnSort}
                        {...(column.type === 'money' || column.type === 'integer' ? { align: 'right' as const } : {})}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`${row.reference ?? ''}-${index}`}>
                      {columns.map((column) => (
                        /*
                          ⚠ `pt-tabular` on money and integers so figures align
                          on their digits down the column — the same treatment
                          every other amount in this product gets. The value
                          itself is already formatted by the SERVER, with the
                          same formatter the document uses, so the screen and
                          the file cannot round differently.
                        */
                        <td
                          key={column.id}
                          className={column.type === 'money' || column.type === 'integer' ? 'pt-tabular pt-num' : undefined}
                        >
                          {row[column.id] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/*
                WHERE THIS PAGE'S SUBSIDY FIGURES CAME FROM.
                
                ⚠ A note block rather than a per-row marker, and the reason is
                structural: the officer chooses which fields to display, so
                `Applied Rule` may not be a column at all while
                `Subsidy (SGD)` is. There is no cell to hang a footnote marker
                on. The server therefore resolves the disclosure from
                `app.bill`'s own provenance columns regardless of the chosen
                field set, and it is scoped to THIS PAGE's rows — so it is
                attached to the figures the reader can actually see, and it is
                ABSENT (not merely empty) on a page whose rows carry none.

                ⚠ The whole-extract count, over every row the filter selects
                rather than this page, is carried in the downloaded document's
                own notes — see `rateProvenanceReportNotes`.
              */}
              <RateProvenanceNotes
                provenance={preview.data?.rateProvenance ?? []}
                heading="Where the subsidy figures on this page came from"
              />
            </div>
            <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
              Showing {rows.length} of {total} matching bill{total === 1 ? '' : 's'}. Ordered by bill reference,
              ascending: this report accepts no other sort, which is what keeps the page walk complete.
            </p>
            <Pagination pager={pager} label="report rows" total={total} shown={rows.length} />
          </div>
        )
      )}
    </div>
  )
}
